import React from 'react';
import { ResponsiveScatterPlot } from '@nivo/scatterplot';
import { linearGradientDef } from '@nivo/core';
import { adjustColor, generateGradientsAndColors } from './utils';

const nivoScatterPlotTheme = {
  axis: {
    ticks: {
      text: {
        fontSize: 12,
        fontWeight: '600',
        fontFamily: 'Inter, sans-serif',
      },
    },
  },
  legends: {
    text: {
      fontSize: 12,
    },
  },
  tooltip: {
    container: {
      background: 'white',
      color: 'inherit',
      fontSize: 'inherit',
      borderRadius: '2px',
      boxShadow: '0 1px 2px rgba(0, 0, 0, 0.25)',
      padding: '5px 9px',
    },
    basic: {
      whiteSpace: 'pre',
      display: 'flex',
      alignItems: 'center',
    },
    chip: {
      marginRight: '7px',
    },
    table: {},
    tableCell: {
      padding: '3px 5px',
    },
  },
};

const getXYRange = (xValues, yValues) => {
  const xMin = Math.min(...xValues);
  const xMax = Math.max(...xValues);
  const yMin = Math.min(...yValues);
  const yMax = Math.max(...yValues);
  const xRange = xMax - xMin;
  const yRange = yMax - yMin;
  return { xMin, xMax, yMin, yMax, xRange, yRange };
};



/**
 * NivoScatterChart component renders a customizable scatter plot using Nivo's ResponsiveScatterPlot.
 * The chart supports custom colors, axis labels, label positioning, padding, and optional data labels.
 *
 * @param {Array<Object>} data - The data to plot in the scatter chart. Each object represents a series with multiple data points.
 *                                Example:
 *                                [
 *                                  {
 *                                    id: "My Series", // A unique identifier for the series
 *                                    data: [
 *                                      { x: 5, y: 10, label: "A" }, // Each point has x, y values and an optional label
 *                                      { x: 15, y: 20, label: "B" },
 *                                      { x: 25, y: 30, label: "C" },
 *                                    ],
 *                                  },
 *                                  {
 *                                    id: "Another Series",
 *                                    data: [
 *                                      { x: 8, y: 15, label: "X" },
 *                                      { x: 18, y: 25, label: "Y" },
 *                                    ],
 *                                  },
 *                                ]
 *
 * @param {Array<string>} keys - The series identifiers used for color mapping. Each key corresponds to a unique series id in the data array.
 *                                Example: ["My Series", "Another Series"]
 * @param {Array<string>} [customColors] - Optional array of custom colors to apply to the series. If not provided, colors are auto-generated.
 *                                          Example: ["#FF6384", "#36A2EB"]
 * @param {string} [axisBottomLegend=''] - The legend text for the bottom (X) axis.
 *                                          Example: "X Axis Label"
 * @param {string} [axisLeftLegend=''] - The legend text for the left (Y) axis.
 *                                        Example: "Y Axis Label"
 * @param {number} [symbolSize=10] - Size of the legend symbols (in pixels).
 * @param {number} [xPadding=1] - Padding to add to the X-axis range based on data range, as a multiplier.
 *                                 Example: 1.1 for 10% padding.
 * @param {number} [yPadding=1] - Padding to add to the Y-axis range based on data range, as a multiplier.
 * @param {boolean} [showLabels=false] - Determines if data point labels should be displayed on the scatter plot.
 * @param {string} [labelPosition='top'] - Position of labels relative to the points. Accepts 'top' or 'bottom'.
 *                                          Example: "bottom"
 * @param {number|function} [dotSize=10] - Size of the dots representing each data point. Accepts a fixed number or a function to determine dynamic sizes.
 *                                         Example: 12 or (node) => node.data.value * 2
 * @param {Array<number>} [xBounds] - Optional fixed bounds for the X-axis in the form [min, max].
 *                                     Example: [0, 100]
 * @param {Array<number>} [yBounds] - Optional fixed bounds for the Y-axis in the form [min, max].
 *                                     Example: [0, 50]
 *
 * @returns {JSX.Element} A Nivo ResponsiveScatterPlot component wrapped with optional labels and customization options.
 *
 * Example Usage:
 * ```
 * import NivoScatterChart from './NivoScatterChart';
 *
 * const data = [
 *   {
 *     id: "Series A",
 *     data: [
 *       { x: 10, y: 20, label: "Point 1" },
 *       { x: 15, y: 25, label: "Point 2" },
 *     ],
 *   },
 *   {
 *     id: "Series B",
 *     data: [
 *       { x: 5, y: 10, label: "A" },
 *       { x: 20, y: 30, label: "B" },
 *     ],
 *   },
 * ];
 *
 * const customColors = ["#FF5733", "#33FFCE"];
 *
 * <NivoScatterChart
 *   data={data}
 *   keys={["Series A", "Series B"]}
 *   customColors={customColors}
 *   axisBottomLegend="X Axis"
 *   axisLeftLegend="Y Axis"
 *   symbolSize={12}
 *   xPadding={1.2}
 *   yPadding={1.2}
 *   showLabels={true}
 *   labelPosition="top"
 *   dotSize={(node) => node.size * 1.5}
 *   xBounds={[0, 30]}
 *   yBounds={[0, 35]}
 * />
 * ```
 */
const NivoScatterChart = ({
  data,
  keys,
  customColors,
  axisBottomLegend = '',
  axisLeftLegend = '',
  symbolSize = 10,
  xPadding = 1,
  yPadding = 1,
  showLabels = false,
  labelPosition = 'top',
  dotSize = 10,
  xBounds,
  yBounds
}) => {
  const { gradients, colorMapping } = generateGradientsAndColors(keys, customColors);
  const colorsToUse = customColors || colorMapping;

  const xValues = data.reduce((acc, series) => acc.concat(series.data.map(d => d.x)), []);
  const yValues = data.reduce((acc, series) => acc.concat(series.data.map(d => d.y)), []);

  let { xMin, xMax, yMin, yMax, xRange, yRange } = getXYRange(xValues, yValues);
  if (Array.isArray(xBounds) && xBounds.length === 2) {
    xMin = xBounds[0];
    xMax = xBounds[1];
    xRange = xMax - xMin;
  }
  if (Array.isArray(yBounds) && yBounds.length === 2) {
    yMin = yBounds[0];
    yMax = yBounds[1];
    yRange = yMax - yMin;
  }

  // Custom layer to add labels to the nodes
  const LabelsLayer = ({ nodes }) => (
    <>
      {showLabels &&
        nodes.map(node => (
          node.data.label ? (
            <text
              key={`${node.serieId}-${node.id}`}
              x={node.x}
              y={node.y + (labelPosition === 'top' ? -node.size : node.size + 4)}
              textAnchor="middle"
              alignmentBaseline={labelPosition === 'top' ? 'baseline' : 'hanging'}
              style={{ fontSize: '10px', fill: '#666', fontWeight: 'bold' }}
            >
              {node.data.label}
            </text>
          ) : null
        ))}
    </>
  );

  return (
    <ResponsiveScatterPlot
      data={data}
      nodeSize={typeof dotSize === 'function' ? dotSize : () => dotSize}
      xScale={{
        type: 'linear',
        min: xMin === 0 ? 0 : xMin - xRange * xPadding,
        max: xMax + xRange * xPadding,
      }}
      yScale={{
        type: 'linear',
        min: yMin === 0 ? 0 : yMin - yRange * yPadding,
        max: yMax + yRange * yPadding,
      }}
      theme={nivoScatterPlotTheme}
      margin={{ top: 20, right: 50, bottom: 50, left: 50 }}
      blendMode="multiply"
      colors={node => colorsToUse[node.serieId]}
      axisTop={null}
      axisRight={null}
      gridYValues={5}
      gridXValues={5}
      axisBottom={{
        orient: 'bottom',
        tickSize: 5,
        tickPadding: 5,
        tickRotation: 0,
        legend: axisBottomLegend,
        legendPosition: 'middle',
        legendOffset: 46,
      }}
      axisLeft={{
        orient: 'left',
        tickSize: 5,
        tickPadding: 5,
        tickRotation: 0,
        legend: axisLeftLegend,
        legendPosition: 'middle',
        legendOffset: -40,
      }}
      legends={[
        {
          anchor: 'top-right',
          direction: 'column',
          justify: false,
          translateX: 130,
          translateY: 0,
          itemsSpacing: 0,
          itemWidth: 250,
          itemHeight: 20,
          itemTextColor: '#999',
          symbolSize: symbolSize,
          symbolShape: 'circle',
          effects: [
            {
              on: 'hover',
              style: {
                itemBackground: 'rgba(0, 0, 0, .03)',
                itemOpacity: 1,
              },
            },
          ],
        },
      ]}
      defs={gradients}
      fill={keys.map((key, index) => ({
        match: { id: key },
        id: `gradient${index}`,
      }))}
      tooltip={({ node }) => (
        <div>
          <strong style={{ color: colorMapping[node.serieId] }}>
            {node.serieId}:
          </strong>
          <br />
          x: {node.data.xFormatted}, y: {node.data.yFormatted}
        </div>
      )}
      animate={true}
      motionStiffness={90}
      motionDamping={15}
      useMesh={true}
      layers={[
        'grid',
        'axes',
        'nodes',
        LabelsLayer,
        'markers',
        'legends',
      ]}
    />
  );
};

export default NivoScatterChart;
